import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Testimonials — Phase B (API-driven with hardcoded fallback).
 * Fetches from /api/testimonials on mount. If the fetch fails, network is
 * down, or the response is empty, we keep rendering the hardcoded FEATURE +
 * OTHERS arrays from Phase A — so the section never disappears.
 */

const FEATURE = {
    name: "Fontaenia V.",
    role: "After her first class",
    stars: 5,
    quote:
        "Thank you so much! I learned so much today and I can't wait to learn more. You are a great instructor, a patient one too with all of my questions. Thank you for everything!",
};

const OTHERS = [
    {
        name: "Hue M.",
        role: "Returning student",
        stars: 5,
        quote:
            "Brother, I feel extra confident with the golden nuggets you're teaching! Can't wait for the next class. Ya making it make sense.",
    },
    {
        name: "Bob L.",
        role: "First session",
        stars: 5,
        quote:
            "I just had my 1st training and it was AWESOME. I can't wait to learn more. He knows what he's talking about!",
    },
    {
        name: "Yuri C.",
        role: "10/10 would recommend",
        stars: 5,
        quote:
            "What I had difficulty understanding for years, I'm FINALLY understanding. So grateful for just the first session — I've been addicted to learning more and more.",
    },
    {
        name: "Nikki S.",
        role: "Community member",
        stars: 5,
        quote:
            "Looking forward to this type of training. Can't wait to applaud your next success — congrats on the program.",
    },
];

const DISCLAIMER =
    "Individual experiences. Testimonials reflect the personal experiences of individual students and are not a guarantee of results. Swing Trust is an educational and simulation program and does not provide financial or investment advice, and does not guarantee any income, profit, or trading outcome. Results are not typical. Trading involves substantial risk of loss.";

// Small, local, presentational — inline per instructions (no new files).
function InitialAvatar({ name, size = "md" }) {
    const initial = (name || "?").trim().charAt(0).toUpperCase();
    const dims =
        size === "lg"
            ? "w-14 h-14 text-xl"
            : "w-11 h-11 text-base";
    return (
        <div
            aria-hidden="true"
            className={`${dims} rounded-full flex items-center justify-center font-serif flex-shrink-0`}
            style={{ background: "var(--st-royal)", color: "var(--st-white)" }}
        >
            {initial}
        </div>
    );
}

function StarRow({ count = 5 }) {
    return (
        <div className="flex items-center gap-0.5" aria-label={`${count} out of 5 stars`}>
            {Array.from({ length: count }).map((_, i) => (
                <Star
                    key={i}
                    size={16}
                    strokeWidth={0}
                    fill="var(--st-royal)"
                    aria-hidden="true"
                />
            ))}
        </div>
    );
}

export default function Testimonials() {
    // Initial state = hardcoded fallback. The section is always visible even
    // before the API responds, and stays visible if the API fails.
    const [feature, setFeature] = useState(FEATURE);
    const [others, setOthers] = useState(OTHERS);

    useEffect(() => {
        let cancelled = false;
        api.publicTestimonials()
            .then((rows) => {
                if (cancelled) return;
                if (!Array.isArray(rows) || rows.length === 0) return; // keep fallback
                const featured = rows.filter((r) => r.is_featured);
                if (featured.length > 1) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        "[Testimonials] multiple featured rows returned; picking first by order",
                        featured.map((r) => r.id),
                    );
                }
                const featuredPick = featured.length
                    ? [...featured].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0]
                    : null;
                const rest = rows
                    .filter((r) => !featuredPick || r.id !== featuredPick.id)
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                setFeature(featuredPick); // may be null → no feature slot
                setOthers(rest);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.warn(
                    "[Testimonials] falling back to hardcoded content — API fetch failed:",
                    err?.message || err,
                );
            });
        return () => { cancelled = true; };
    }, []);

    return (
        <section
            className="py-20 md:py-24"
            style={{ background: "var(--st-white)" }}
            aria-labelledby="testimonials-heading"
            data-testid="testimonials-section"
        >
            <div className="max-w-6xl mx-auto px-6">
                {/* Section header */}
                <div className="max-w-2xl">
                    <span
                        className="tracking-caps text-[11px] font-semibold"
                        style={{ color: "var(--st-royal)" }}
                        data-testid="testimonials-eyebrow"
                    >
                        FROM THE CLASSROOM
                    </span>
                    <h2
                        id="testimonials-heading"
                        className="font-serif text-3xl md:text-4xl mt-3"
                        style={{ color: "var(--st-navy)" }}
                        data-testid="testimonials-heading"
                    >
                        Students who put in the work.
                    </h2>
                    <p
                        className="mt-4 text-base leading-relaxed"
                        style={{ color: "var(--st-ink-muted)" }}
                        data-testid="testimonials-subhead"
                    >
                        Real reactions after live sessions inside Swing Trust. What they have in common isn't a dollar figure — it's finally understanding the material.
                    </p>
                </div>

                {/* Feature card (only if one is featured) */}
                {feature && (
                    <article
                        className="st-surface p-8 md:p-10 mt-12 motion-safe:transition-transform motion-safe:duration-300 motion-safe:hover:-translate-y-0.5"
                        data-testid="testimonial-card-feature"
                    >
                        <div className="flex flex-col md:flex-row md:items-start gap-6">
                            <InitialAvatar name={feature.name} size="lg" />
                            <div className="flex-1">
                                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                                    <div className="font-serif text-xl" style={{ color: "var(--st-navy)" }}>
                                        {feature.name}
                                    </div>
                                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-silver-500)" }}>
                                        {feature.role}
                                    </div>
                                </div>
                                <div className="mt-2">
                                    {Number(feature.stars ?? 0) > 0 && <StarRow count={Number(feature.stars)} />}
                                </div>
                                <blockquote className="mt-5 font-serif italic text-lg md:text-xl leading-relaxed" style={{ color: "var(--st-ink)" }}>
                                    “{feature.quote}”
                                </blockquote>
                            </div>
                        </div>
                    </article>
                )}

                {/* Remaining cards: 2×N grid on md+, single column on mobile */}
                {others.length > 0 && (
                    <div className={`${feature ? "mt-6" : "mt-12"} grid md:grid-cols-2 gap-6`} data-testid="testimonials-grid">
                        {others.map((t, i) => (
                            <article
                                key={t.id || t.name}
                                className="st-surface p-7 flex flex-col motion-safe:transition-transform motion-safe:duration-300 motion-safe:hover:-translate-y-0.5"
                                data-testid={`testimonial-card-${i + 1}`}
                            >
                                <div className="flex items-center gap-4">
                                    <InitialAvatar name={t.name} />
                                    <div className="min-w-0">
                                        <div className="font-serif text-lg truncate" style={{ color: "var(--st-navy)" }}>
                                            {t.name}
                                        </div>
                                        <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-silver-500)" }}>
                                            {t.role}
                                        </div>
                                    </div>
                                </div>
                                {Number(t.stars ?? 0) > 0 && (
                                    <div className="mt-3">
                                        <StarRow count={Number(t.stars)} />
                                    </div>
                                )}
                                <blockquote className="mt-5 text-base leading-relaxed flex-1" style={{ color: "var(--st-ink)" }}>
                                    “{t.quote}”
                                </blockquote>
                            </article>
                        ))}
                    </div>
                )}

                {/* Disclaimer */}
                <p
                    className="mt-10 text-xs leading-relaxed italic max-w-4xl"
                    style={{ color: "var(--st-ink-muted)" }}
                    data-testid="testimonials-disclaimer"
                >
                    {DISCLAIMER}
                </p>
            </div>
        </section>
    );
}
